import React, { useState, useMemo } from 'react';
import tw, { styled } from 'twin.macro';
import { Warning, XCircle, CaretDown, CaretRight } from '@phosphor-icons/react';
import { AdChannel, AdPlacement } from 'types/adTypes';
import { AdVariant } from 'types/adEditorTypes';
import {
  getFieldWarningMessage,
  getEmojiWarningMessage,
  getAllChannelPlacementRestrictions,
  ChannelRestriction,
} from 'utils/channelRestrictions';
import useNavigationContext from 'hooks/context/nav-context';

interface ChannelRestrictionsSummaryProps {
  variant: AdVariant;
  className?: string;
}

interface WarningItem {
  field: string;
  channel: AdChannel;
  placement: AdPlacement;
  message: string;
  isError: boolean;
}

const ChannelRestrictionsSummary: React.FC<ChannelRestrictionsSummaryProps> = ({
  variant,
  className,
}) => {
  const { currentVacancy } = useNavigationContext();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const warnings = useMemo(() => {
    const allRestrictions = getAllChannelPlacementRestrictions();
    const warnings: WarningItem[] = [];
    const currentPlacement = variant.placement;

    const filteredRestrictions = allRestrictions
      .filter(({ placement }) => placement === currentPlacement)
      .filter(({ channel }) => currentVacancy?.channels.includes(channel));

    filteredRestrictions.forEach(({ channel, placement, restriction }) => {
      // Dynamically check all string fields in the variant
      Object.entries(restriction).forEach(([fieldKey, fieldRestriction]) => {
        const variantValue = variant[fieldKey as keyof AdVariant];

        // Only check string fields that have content
        if (
          typeof variantValue === 'string' &&
          variantValue &&
          fieldRestriction
        ) {
          // Check both length and emoji restrictions
          const restrictionTypes = [
            { key: 'maxLength', getMessage: getFieldWarningMessage },
            { key: 'maxEmojis', getMessage: getEmojiWarningMessage },
          ] as const;

          restrictionTypes.forEach(({ key, getMessage }) => {
            const restrictionRule = fieldRestriction[key];
            if (restrictionRule) {
              const warningMessage = getMessage(
                variantValue,
                fieldKey as keyof ChannelRestriction,
                channel,
                placement
              );
              if (warningMessage) {
                warnings.push({
                  field: fieldKey.charAt(0).toUpperCase() + fieldKey.slice(1),
                  channel,
                  placement,
                  message: warningMessage,
                  isError: restrictionRule.level === 'error',
                });
              }
            }
          });
        }
      });
    });

    return warnings;
  }, [
    variant.text,
    variant.headline,
    variant.description,
    variant.placement,
    currentVacancy?.channels,
  ]);

  if (warnings.length === 0) return null;

  // Group warnings by channel, then by field
  const groupedWarnings = warnings.reduce((acc, warning) => {
    const channelKey = warning.channel;
    if (!acc[channelKey]) {
      acc[channelKey] = {};
    }
    if (!acc[channelKey][warning.field]) {
      acc[channelKey][warning.field] = [];
    }
    acc[channelKey][warning.field].push(warning);
    return acc;
  }, {} as Record<string, Record<string, WarningItem[]>>);

  return (
    <Container className={className}>
      <Header onClick={() => setIsCollapsed(!isCollapsed)}>
        <div tw="flex items-center gap-2">
          {isCollapsed ? (
            <CaretRight size={16} weight="fill" color="#6b7280" />
          ) : (
            <CaretDown size={16} weight="fill" color="#6b7280" />
          )}
          <Warning size={16} weight="fill" color="#6b7280" />
          <span>
            This variant has {warnings.length} issue
            {warnings.length !== 1 ? 's' : ''}.
          </span>
        </div>
      </Header>
      {!isCollapsed && (
        <Content>
          {Object.entries(groupedWarnings).map(([channel, fieldGroups]) => (
            <ChannelSection key={channel}>
              <ChannelName>{channel}</ChannelName>
              {Object.entries(fieldGroups).map(([field, fieldWarnings]) => (
                <FieldSection key={field}>
                  <FieldName>{field} :</FieldName>
                  <WarningsList>
                    {fieldWarnings.map((warning, index) => (
                      <WarningRow key={index}>
                        <WarningIcon>
                          {warning.isError ? (
                            <XCircle size={14} weight="fill" color="#ef4444" />
                          ) : (
                            <Warning size={14} weight="fill" color="#f59e0b" />
                          )}
                        </WarningIcon>
                        <WarningText>{warning.message}</WarningText>
                      </WarningRow>
                    ))}
                  </WarningsList>
                </FieldSection>
              ))}
            </ChannelSection>
          ))}
        </Content>
      )}
    </Container>
  );
};

const Container = styled.div`
  ${tw`bg-white border border-gray-200 rounded-lg shadow-sm`}
`;

const Header = styled.div`
  ${tw`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg cursor-pointer`}
  span {
    ${tw`font-medium text-base text-gray-700`}
  }
`;

const Content = styled.div`
  ${tw`p-4 space-y-4`}
`;

const ChannelSection = styled.div`
  ${tw`space-y-2`}
`;

const ChannelName = styled.h4`
  ${tw`font-semibold text-sm text-gray-800`}
`;

const FieldSection = styled.div`
  ${tw`bg-gray-50 rounded-md p-3 space-y-2`}
`;

const FieldName = styled.h5`
  ${tw`text-sm text-gray-700`}
`;

const WarningsList = styled.div`
  ${tw`space-y-1.5`}
`;

const WarningRow = styled.div`
  ${tw`flex items-center gap-2`}
`;

const WarningIcon = styled.div`
  ${tw`flex-shrink-0`}
`;

const WarningText = styled.span`
  ${tw`text-sm text-gray-700`}
`;

export default ChannelRestrictionsSummary;
